/**
 * Utilitários para exportação de dados em múltiplos formatos
 */

/**
 * Exporta dados para CSV
 */
export const exportToCSV = (
  data: any[],
  filename: string,
  headers?: string[]
): void => {
  if (!data || data.length === 0) {
    console.warn('Nenhum dado para exportar');
    return;
  }

  // Usar headers fornecidos ou extrair do primeiro objeto
  const csvHeaders = headers || Object.keys(data[0]);
  
  // Criar linhas CSV
  const csvRows = [
    csvHeaders.join(','), // Header
    ...data.map(row =>
      csvHeaders.map(header => {
        const value = row[header] ?? '';
        // Escapar vírgulas e aspas
        if (typeof value === 'string' && (value.includes(',') || value.includes('"') || value.includes('\n'))) {
          return `"${value.replace(/"/g, '""')}"`;
        }
        return value;
      }).join(',')
    ),
  ];

  const csv = csvRows.join('\n');
  
  // BOM para UTF-8 (Excel reconhece melhor)
  const BOM = '\uFEFF';
  const blob = new Blob([BOM + csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Exporta dados para Excel usando formato XML (compatível com Excel)
 * Não requer bibliotecas externas
 */
export const exportToExcel = (
  data: any[],
  filename: string,
  headers?: string[],
  sheetName: string = 'Sheet1'
): void => {
  if (!data || data.length === 0) {
    console.warn('Nenhum dado para exportar');
    return;
  }

  const excelHeaders = headers || Object.keys(data[0]);
  
  // Criar XML para Excel
  let xml = '<?xml version="1.0"?>\n';
  xml += '<?mso-application progid="Excel.Sheet"?>\n';
  xml += '<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"\n';
  xml += ' xmlns:o="urn:schemas-microsoft-com:office:office"\n';
  xml += ' xmlns:x="urn:schemas-microsoft-com:office:excel"\n';
  xml += ' xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet"\n';
  xml += ' xmlns:html="http://www.w3.org/TR/REC-html40">\n';
  xml += `<Worksheet ss:Name="${sheetName}">\n`;
  xml += '<Table>\n';
  
  // Headers
  xml += '<Row>\n';
  excelHeaders.forEach(header => {
    xml += '<Cell><Data ss:Type="String">' + escapeXml(header) + '</Data></Cell>\n';
  });
  xml += '</Row>\n';
  
  // Data rows
  data.forEach(row => {
    xml += '<Row>\n';
    excelHeaders.forEach(header => {
      const value = row[header] ?? '';
      const type = typeof value === 'number' ? 'Number' : 'String';
      xml += `<Cell><Data ss:Type="${type}">${escapeXml(String(value))}</Data></Cell>\n`;
    });
    xml += '</Row>\n';
  });
  
  xml += '</Table>\n';
  xml += '</Worksheet>\n';
  xml += '</Workbook>';
  
  const blob = new Blob([xml], { type: 'application/vnd.ms-excel' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}.xls`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Exporta dados para PDF usando API do navegador (window.print)
 * Para PDFs mais complexos, considere usar uma biblioteca como jsPDF
 */
export const exportToPDF = (
  title: string,
  content: string | HTMLElement,
  filename: string
): void => {
  // Criar uma janela temporária com o conteúdo
  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    console.error('Não foi possível abrir janela de impressão');
    return;
  }

  const htmlContent = typeof content === 'string' 
    ? content 
    : content.outerHTML;

  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <title>${title}</title>
        <style>
          @media print {
            @page {
              margin: 1cm;
            }
            body {
              font-family: Arial, sans-serif;
              font-size: 12px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 20px;
            }
            th, td {
              border: 1px solid #ddd;
              padding: 8px;
              text-align: left;
            }
            th {
              background-color: #f2f2f2;
              font-weight: bold;
            }
            h1 {
              color: #333;
              margin-bottom: 20px;
            }
          }
        </style>
      </head>
      <body>
        <h1>${title}</h1>
        <div>${htmlContent}</div>
      </body>
    </html>
  `);
  
  printWindow.document.close();
  
  // Aguardar carregamento e abrir diálogo de impressão
  printWindow.onload = () => {
    setTimeout(() => {
      printWindow.print();
      // Fechar após impressão (opcional)
      // printWindow.close();
    }, 250);
  };
};

/**
 * Escapa caracteres XML especiais
 */
const escapeXml = (unsafe: string): string => {
  return unsafe
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
};

/**
 * Exporta dados para JSON
 */
export const exportToJSON = (
  data: any,
  filename: string
): void => {
  const json = JSON.stringify(data, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}.json`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

